import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, RotateCw, Bell, Mail, MessageCircle, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { useDebounced } from "@/lib/useDebounced";
import { formatFechaHora } from "@/lib/format";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/common/ErrorState";

const PAGE_SIZE = 25;

const ESTADO_ESTILOS = {
  enviada: "bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]",
  pendiente: "bg-amber-50 text-amber-600",
  fallida: "bg-red-50 text-red-600",
};

const CANAL_ICONO = { whatsapp: MessageCircle, email: Mail, mock: FlaskConical };

export default function Notificaciones() {
  usePageTitle("Notificaciones");
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [canal, setCanal] = useState("");
  const [page, setPage] = useState(1);
  const [reintentando, setReintentando] = useState(null);
  const qDebounced = useDebounced(q, 300);

  useEffect(() => setPage(1), [qDebounced, estado, canal]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-notificaciones", qDebounced, estado, canal, page],
    queryFn: () =>
      adminApi
        .get("/admin/notificaciones", {
          params: { q: qDebounced || undefined, estado: estado || undefined, canal: canal || undefined, page, page_size: PAGE_SIZE },
        })
        .then((r) => r.data),
  });

  const reintentar = async (id) => {
    setReintentando(id);
    try {
      await adminApi.post(`/admin/notificaciones/${id}/reintentar`);
      toast.success("Reintento en curso");
      refetch();
      setTimeout(refetch, 3000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReintentando(null);
    }
  };

  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-5xl px-8 py-8">
      <h1 className="text-xl font-semibold text-[var(--tc-text)]">Notificaciones</h1>
      <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">Historial de avisos por cambio de etapa. Reintentá los fallidos.</p>

      <div className="mt-5 flex flex-wrap gap-2.5">
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tc-text-secondary)]" />
          <input
            data-testid={ADMIN.notificacionesSearchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por VIN"
            className="w-52 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-[var(--tc-accent)]"
          />
        </div>
        <select data-testid={ADMIN.notificacionesFilterEstado} value={estado} onChange={(e) => setEstado(e.target.value)} className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm text-[var(--tc-text)] outline-none focus:border-[var(--tc-accent)]">
          <option value="">Todos los estados</option>
          <option value="enviada">Enviada</option>
          <option value="pendiente">Pendiente</option>
          <option value="fallida">Fallida</option>
        </select>
        <select data-testid={ADMIN.notificacionesFilterCanal} value={canal} onChange={(e) => setCanal(e.target.value)} className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm text-[var(--tc-text)] outline-none focus:border-[var(--tc-accent)]">
          <option value="">Todos los canales</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
          <option value="mock">Mock</option>
        </select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-11 w-full rounded-[var(--tc-radius)]" />)}
          </div>
        ) : isError ? (
          <ErrorState testId="admin-notificaciones-error" mensaje="No se pudo cargar el historial. Intentá de nuevo." onRetry={refetch} />
        ) : !data || data.resultados.length === 0 ? (
          <div data-testid={ADMIN.notificacionesEmptyState} className="flex flex-col items-center py-16 text-center">
            <Bell size={26} className="text-[var(--tc-state-pending)]" />
            <p className="mt-3 text-sm text-[var(--tc-text-secondary)]">No hay notificaciones con esos filtros.</p>
          </div>
        ) : (
          <table className="w-full min-w-[880px] text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead>
              <tr className="border-b border-[var(--tc-border)] text-left text-xs font-medium uppercase tracking-wide text-[var(--tc-text-secondary)]">
                <th className="px-4 py-3">Unidad</th>
                <th className="px-4 py-3">Etapa</th>
                <th className="px-4 py-3">Canal</th>
                <th className="px-4 py-3">Destinatario</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {data.resultados.map((n) => {
                const Icono = CANAL_ICONO[n.canal] || Bell;
                return (
                  <tr key={n.id} data-testid={ADMIN.notificacionesRow} className="border-b border-[var(--tc-border)] last:border-0">
                    <td className="px-4 py-3">
                      {n.unidad ? (
                        <Link to={`/admin/unidades/${n.unidad_id}`} className="font-medium text-[var(--tc-text)] hover:text-[var(--tc-accent)]">
                          {n.unidad.marca} {n.unidad.modelo} <span className="font-mono text-xs text-[var(--tc-text-secondary)]">...{n.unidad.vin_last6}</span>
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-[var(--tc-text-secondary)]">{n.etapa?.nombre || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-[var(--tc-text-secondary)]"><Icono size={14} /> {n.canal}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--tc-text-secondary)]">
                      {n.destinatario_nombre && <p className="font-medium text-[var(--tc-text)]">{n.destinatario_nombre}</p>}
                      <p className="font-mono">{n.destinatario}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${ESTADO_ESTILOS[n.estado] || "bg-gray-100 text-gray-500"}`}>{n.estado}</span>
                      {n.respuesta_proveedor && <p className="mt-1 max-w-[220px] truncate text-[11px] text-[var(--tc-text-secondary)]" title={n.respuesta_proveedor}>{n.respuesta_proveedor}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs tabular-nums text-[var(--tc-text-secondary)]">{formatFechaHora(n.creado_en)}</td>
                    <td className="px-4 py-3 text-right">
                      {n.estado === "fallida" && (
                        <button
                          data-testid={ADMIN.notificacionesRetryButton}
                          onClick={() => reintentar(n.id)}
                          disabled={reintentando === n.id}
                          className="inline-flex items-center gap-1.5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-1.5 text-xs font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] disabled:opacity-50"
                        >
                          <RotateCw size={12} className={reintentando === n.id ? "animate-spin" : ""} /> Reintentar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-[var(--tc-text-secondary)]">Página {page} de {totalPaginas}</p>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-1.5 text-sm disabled:opacity-40">Anterior</button>
            <button disabled={page >= totalPaginas} onClick={() => setPage((p) => p + 1)} className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-1.5 text-sm disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      )}
    </div>
  );
}
